import { useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import { formatDate } from "../../utils/formatters";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";

// Calculate section score based on passes and failures
const calculateSectionScore = (items, type) => {
  if (!items || items.length === 0) return 100;

  if (type === "violations") {
    let penalty = 0;
    items.forEach((item) => {
      const impact = item.impact?.toLowerCase();
      const nodeCount = item.nodes?.length || 1;
      if (impact === "critical") penalty += 15 * nodeCount;
      else if (impact === "serious") penalty += 10 * nodeCount;
      else if (impact === "moderate") penalty += 5 * nodeCount;
      else penalty += 2 * nodeCount;
    });
    return Math.max(0, 100 - penalty);
  }
  return 100;
};

// Status Icon Component
const StatusIcon = ({ type, score }) => {
  if (type === "pass" || score === 100) {
    return (
      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
        <Icon icon="mdi:check" className="text-green-600 text-sm" />
      </div>
    );
  }
  if (type === "fail" || score === 0) {
    return (
      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
        <Icon icon="mdi:close" className="text-red-600 text-sm" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <div className="w-3 h-3 rounded-full border-2 border-gray-500"></div>
    </div>
  );
};

// Score Badge Component
const ScoreBadge = ({ score }) => {
  const getBadgeColor = () => {
    if (score >= 90) return "bg-green-100 text-green-700";
    if (score >= 70) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <span
      className={`px-3 py-1 rounded text-sm font-medium ${getBadgeColor()}`}
    >
      Score: {score}
    </span>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, total, type }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const barColor = type === "failures" ? "bg-red-500" : "bg-green-500";

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-medium text-gray-800">{value}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Single Result Item Component
const ResultItem = ({ item, type }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const nodes = item.nodes || [];
  const failureCount = type === "violation" ? nodes.length : 0;
  const successCount = type === "pass" ? nodes.length : 0;
  const totalElements = nodes.length;

  const getStatusType = () => {
    if (type === "pass") return "pass";
    if (type === "violation") return "fail";
    return "neutral";
  };

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 py-3 px-4 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <StatusIcon type={getStatusType()} />

        <span className="flex-1 text-sm text-gray-800 font-medium">
          {item.help || item.id}
        </span>

        <div className="flex items-center gap-3">
          {type === "violation" && (
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                item.impact === "critical"
                  ? "bg-red-100 text-red-700"
                  : item.impact === "serious"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-yellow-100 text-yellow-700"
              }`}
            >
              {item.impact}
            </span>
          )}
          <Icon
            icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
            className="text-gray-400 text-xl flex-shrink-0"
          />
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col lg:flex-row">
            {/* Left Column - Main Content */}
            <div className="flex-1 p-4 border-b lg:border-b-0 lg:border-r border-gray-200">
              {/* Requirement */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Requirement:
                </h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>

              {/* Code Snapshots */}
              {nodes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    {nodes.length} Code snapshot{nodes.length > 1 ? "s" : ""} of{" "}
                    {type === "violation" ? "failed" : "successful"} elements
                  </h4>
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {nodes.slice(0, 10).map((node, nodeIdx) => (
                      <div key={nodeIdx} className="space-y-2">
                        <div className="flex items-start gap-2">
                          <span
                            className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-medium text-white ${type === "violation" ? "bg-red-500" : "bg-green-500"}`}
                          >
                            {nodeIdx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <code className="text-xs bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto block mb-2">
                              {node.html}
                            </code>
                            {node.target && (
                              <p className="text-[10px] text-gray-500 font-mono italic">
                                Selector:{" "}
                                {Array.isArray(node.target)
                                  ? node.target.join(" > ")
                                  : node.target}
                              </p>
                            )}
                            {node.failureSummary && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700">
                                <strong>Fix:</strong> {node.failureSummary}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {nodes.length > 10 && (
                      <p className="text-xs text-gray-500 ml-8">
                        + {nodes.length - 10} more elements
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Fixes (WordPress Only) */}
              {item.suggestedFix && item.suggestedFix.length > 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Icon icon="mdi:auto-fix" />
                    AI Suggested Implementation:
                  </h4>
                  <div className="space-y-2">
                    {Array.isArray(item.suggestedFix) ? (
                      item.suggestedFix.map((fix, fIdx) => (
                        <code
                          key={fIdx}
                          className="block text-xs bg-white border border-green-200 text-green-800 p-2 rounded overflow-x-auto"
                        >
                          {fix}
                        </code>
                      ))
                    ) : (
                      <code className="block text-xs bg-white border border-green-200 text-green-800 p-2 rounded overflow-x-auto">
                        {item.suggestedFix}
                      </code>
                    )}
                  </div>
                </div>
              )}

              {/* Help URL */}
              {item.helpUrl && (
                <a
                  href={item.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm flex items-center gap-1"
                >
                  Learn more about this requirement
                  <Icon icon="mdi:chevron-right" />
                </a>
              )}
            </div>

            {/* Right Column - Stats */}
            <div className="w-full lg:w-72 p-4 bg-white">
              {/* Total Elements */}
              {totalElements > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">
                    Element Distribution
                  </h4>
                  <ProgressBar
                    label="Failures"
                    value={failureCount}
                    total={totalElements}
                    type="failures"
                  />
                  <ProgressBar
                    label="Successes"
                    value={successCount}
                    total={totalElements}
                    type="successes"
                  />
                </div>
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Target Standards:
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((tag, tIdx) => (
                      <span
                        key={tIdx}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded border border-gray-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category, items, type }) => {
  const sectionScore = calculateSectionScore(items, type);

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
      {/* Category Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">{category}</h3>
        <ScoreBadge score={sectionScore} />
      </div>

      {/* Items */}
      {items.map((item, index) => (
        <ResultItem
          key={`${item.id}-${index}`}
          item={item}
          type={
            type === "violations"
              ? "violation"
              : type === "passes"
                ? "pass"
                : "neutral"
          }
        />
      ))}
    </div>
  );
};

const CompleteScanResponseModal = ({
  scanData,
  connection,
  onApplyFixes,
  onGetFixes,
  isGettingFixes,
  isApplyingFixes,
  isFixesApplied,
  shopifyExtensionStatus,
  isCheckingShopifyExtension,
  onEnableShopifyExtension,
}) => {
  const [activeTab, setActiveTab] = useState("violations");

  const data = scanData?.data || scanData;
  if (!data) return null;

  const isShopify = connection?.platform === "shopify";
  const isShopifyExtensionDisabled =
    isShopify && shopifyExtensionStatus && !shopifyExtensionStatus.enabled;

  const violations = data.violations || [];
  const passes = data.passes || [];
  const incomplete = data.incomplete || [];
  const inapplicable = data.inapplicable || [];
  const summary = data.summary || {};

  const totalViolations = summary.totalViolations ?? violations.length;
  const totalIssues = summary.totalIssues ?? null;
  const totalPasses = summary.totalPasses ?? passes.length;
  const totalIncomplete = summary.totalIncomplete ?? incomplete.length;
  const totalInapplicable = summary.totalInapplicable ?? inapplicable.length;
  const complianceScore =
    summary.complianceScore ??
    (violations.length === 0 ? 100 : Math.max(0, 100 - violations.length * 5));

  // Group items by section/category
  const groupBySection = (items) => {
    const groups = {};
    items.forEach((item) => {
      const section = item.section || "General";
      if (!groups[section]) groups[section] = [];
      groups[section].push(item);
    });
    return groups;
  };

  const violationsBySection = useMemo(
    () => groupBySection(violations),
    [violations],
  );
  const passesBySection = useMemo(() => groupBySection(passes), [passes]);
  const incompleteBySection = useMemo(
    () => groupBySection(incomplete),
    [incomplete],
  );
  const inapplicableBySection = useMemo(
    () => groupBySection(inapplicable),
    [inapplicable],
  );

  const renderCategorySections = (groupedItems, type) => {
    const sections = Object.entries(groupedItems);
    if (sections.length === 0) {
      return (
        <div className="text-center py-12">
          <Icon
            icon="mdi:check-circle"
            className="text-5xl text-green-500 mx-auto mb-3 opacity-20"
          />
          <p className="text-gray-500 text-sm">
            {type === "violations"
              ? "No accessibility violations found!"
              : `No entries for ${type}.`}
          </p>
        </div>
      );
    }
    return sections.map(([section, items]) => (
      <CategorySection
        key={section}
        category={section}
        items={items}
        type={type}
      />
    ));
  };

  const getScoreRingColor = () => {
    if (complianceScore >= 90) return "text-green-500";
    if (complianceScore >= 70) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBgColor = () => {
    if (complianceScore >= 90) return "bg-green-50 border-green-200";
    if (complianceScore >= 70) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(scanData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `accessibility-report-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-2">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:clipboard-check" className="text-gray-600 text-xl" />
          <h2 className="text-lg font-semibold text-gray-800">Scan Results</h2>
        </div>
      </div>

      {/* Summary Section */}
      <div className="p-4 bg-white">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Score Circle */}
          <div
            className={`flex-shrink-0 p-5 rounded-xl border ${getScoreBgColor()} flex flex-col items-center justify-center min-w-[160px]`}
          >
            <div className="relative w-24 h-24">
              <svg
                className="w-24 h-24 transform -rotate-90"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  className={getScoreRingColor()}
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${complianceScore * 2.83} 283`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-bold ${getScoreRingColor()}`}>
                  {complianceScore}%
                </span>
              </div>
            </div>
            <p className="mt-2 text-xs font-bold text-gray-500 uppercase tracking-widest">
              Compliance
            </p>
          </div>

          {/* Right: Details */}
          <div className="flex-1 min-w-0">
            {/* URL */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon icon="mdi:web" className="text-gray-400 text-sm" />
                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                  Audited Location
                </span>
              </div>
              <p className="text-sm font-semibold text-gray-800 break-all leading-tight">
                {data.url}
              </p>
              <p className="text-xs text-gray-400 mt-1 font-medium italic">
                {formatDate(data.timestamp || data.createdAt)}
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {totalIssues !== null && (
                <div className="text-center p-2.5 bg-orange-50 rounded-lg border border-orange-100/50">
                  <p className="text-xl font-black text-orange-600">
                    {totalIssues}
                  </p>
                  <p className="text-[10px] text-orange-700/60 font-bold uppercase">
                    Total Issues
                  </p>
                </div>
              )}
              <div className="text-center p-2.5 bg-red-50 rounded-lg border border-red-100/50">
                <p
                  className={`text-xl font-black ${totalViolations > 0 ? "text-red-600" : "text-gray-400"}`}
                >
                  {totalViolations}
                </p>
                <p className="text-[10px] text-red-700/60 font-bold uppercase">
                  Violations
                </p>
              </div>
              <div className="text-center p-2.5 bg-green-50 rounded-lg border border-green-100/50">
                <p className="text-xl font-black text-green-600">
                  {totalPasses}
                </p>
                <p className="text-[10px] text-green-700/60 font-bold uppercase">
                  Passes
                </p>
              </div>
              <div className="text-center p-2.5 bg-blue-50 rounded-lg border border-blue-100/50">
                <p className="text-xl font-black text-blue-600">
                  {totalIncomplete}
                </p>
                <p className="text-[10px] text-blue-700/60 font-bold uppercase">
                  Incomplete
                </p>
              </div>
              <div className="text-center p-2.5 bg-gray-50 rounded-lg border border-gray-200/50">
                <p className="text-xl font-black text-gray-400">
                  {totalInapplicable}
                </p>
                <p className="text-[10px] text-gray-500/60 font-bold uppercase">
                  N/A
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex flex-wrap gap-2">
              {totalViolations > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onGetFixes}
                  disabled={
                    isGettingFixes || isFixesApplied || isShopifyExtensionDisabled
                  }
                  className="flex items-center gap-1.5 border-gray-200 shadow-none hover:shadow-none"
                >
                  {isGettingFixes ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <Icon icon="mdi:auto-fix" />
                  )}
                  <span>
                    {isGettingFixes
                      ? "Fetching..."
                      : isFixesApplied
                        ? "Fixes Applied"
                        : "Apply Auto Fix"}
                  </span>
                </Button>
              )}
              {isShopify && isCheckingShopifyExtension && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <LoadingSpinner size="xs" />
                  Checking Shopify AI Fixes status...
                </span>
              )}
              {isShopifyExtensionDisabled && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onEnableShopifyExtension}
                  className="flex items-center gap-1.5 border-gray-200"
                >
                  <Icon icon="logos:shopify" />
                  <span>Enable Theme Extension</span>
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={downloadJson}
                className="flex items-center gap-1.5 border-gray-200"
              >
                <Icon icon="mdi:download" />
                <span>Download JSON</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky -top-6 z-10">
        <div className="flex flex-wrap gap-0 overflow-x-auto">
          <button
            onClick={() => setActiveTab("violations")}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-tighter border-b-2 transition-colors ${
              activeTab === "violations"
                ? "border-red-500 text-red-600 bg-red-50/30"
                : "border-transparent text-gray-500 hover:bg-gray-50"
            }`}
          >
            Violations ({totalViolations})
          </button>
          <button
            onClick={() => setActiveTab("passes")}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-tighter border-b-2 transition-colors ${
              activeTab === "passes"
                ? "border-green-500 text-green-600 bg-green-50/30"
                : "border-transparent text-gray-500 hover:bg-gray-50"
            }`}
          >
            Passes ({totalPasses})
          </button>
          {totalIncomplete > 0 && (
            <button
              onClick={() => setActiveTab("incomplete")}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-tighter border-b-2 transition-colors ${
                activeTab === "incomplete"
                  ? "border-blue-500 text-blue-600 bg-blue-50/30"
                  : "border-transparent text-gray-500 hover:bg-gray-50"
              }`}
            >
              Incomplete ({totalIncomplete})
            </button>
          )}
          {totalInapplicable > 0 && (
            <button
              onClick={() => setActiveTab("inapplicable")}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-tighter border-b-2 transition-colors ${
                activeTab === "inapplicable"
                  ? "border-gray-500 text-gray-600 bg-gray-50/30"
                  : "border-transparent text-gray-500 hover:bg-gray-50"
              }`}
            >
              Inapplicable ({totalInapplicable})
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 bg-gray-50/50">
        {activeTab === "violations" &&
          renderCategorySections(violationsBySection, "violations")}
        {activeTab === "passes" &&
          renderCategorySections(passesBySection, "passes")}
        {activeTab === "incomplete" &&
          renderCategorySections(incompleteBySection, "incomplete")}
        {activeTab === "inapplicable" &&
          renderCategorySections(inapplicableBySection, "inapplicable")}
      </div>
    </div>
  );
};

export default CompleteScanResponseModal;
